/**
 * #448 re-open (2026-10-17) — what a reloaded chat does with a trailing,
 * unanswered user message.
 *
 * Prod forensics: the reloads that "lost" a reply happened INSIDE the gap
 * between saving the user message and the stream request reaching the server.
 * No run ever existed, so waiting was pointless and Retry (an edit + a NEW run)
 * was never a recovery. The server now stamps the user message with
 * `metadata.run_started_at` (+ `run_id`) right before the run's first model
 * call, so the page can tell the two cases apart:
 *
 *   running        → the server is answering; show the placeholder, poll, and
 *                    offer Resend only after PENDING_TURN_STALE_MS.
 *   never_started  → nothing will answer; re-send it ONCE automatically
 *                    ("Sending…"), and offer Resend if that did not take.
 *   answered       → nothing to do.
 *
 * There is no expiry: a trailing unanswered message keeps its placeholder or
 * its Resend button however old it is.
 *
 * Pure (no React, no network) so the rules are unit-tested; the in-flight
 * marker helpers wrap sessionStorage in try/catch (private mode, blocked
 * storage).
 */

/** Offer Resend once a STARTED run has produced nothing for this long. */
export const PENDING_TURN_STALE_MS = 3 * 60 * 1000;
/** Poll the conversation this often while a turn is pending. */
export const PENDING_TURN_POLL_MS = 4000;
/** After an automatic re-send, how long "Sending…" waits for the run's stamp
 *  before it gives up and offers Resend. */
export const RESEND_GRACE_MS = 45 * 1000;
/** Only re-send automatically a message this recent. An older orphan (e.g. a
 *  chat abandoned days ago, or one from before the stamp existed) shows Resend
 *  instead — opening a chat must never spend energy behind the user's back. */
export const AUTO_RESEND_MAX_AGE_MS = 15 * 60 * 1000;

const MARKER_PREFIX = 'omnis:auto-resend:';

const toMs = (value) => {
    if (value === null || value === undefined) return null;
    const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
};

/** The trailing message when it is a SAVED user message, else null. */
export function trailingUserMessage(messages) {
    if (!Array.isArray(messages) || messages.length === 0) return null;
    const last = messages[messages.length - 1];
    return last && last.isUser && last.dbId ? last : null;
}

/** When the server started a run for this message (ms), or null. */
export function runStartedAtMs(message) {
    return toMs(message?.metadata?.run_started_at);
}

/** 'answered' | 'running' | 'never_started' for a conversation's messages. */
export function decidePendingTurn(messages) {
    const last = trailingUserMessage(messages);
    if (!last) return 'answered';
    return runStartedAtMs(last) !== null ? 'running' : 'never_started';
}

/**
 * What to show (and do) for a pending turn.
 *
 * @param {object} args
 * @param {object[]} args.messages the conversation's messages
 * @param {number} args.now epoch ms
 * @param {{at:number}|null} args.marker this tab's automatic re-send marker for
 *        the trailing message (see readResendMarker)
 * @returns {null | {decision, message, status: 'waiting'|'sending'|'stale',
 *          autoResend: boolean, force: boolean}}
 *   `force` = Resend must start a new run even though one is stamped (the
 *   stamped run went silent); otherwise the re-send is "only if not started".
 */
export function pendingTurnView({ messages, now, marker = null }) {
    const decision = decidePendingTurn(messages);
    if (decision === 'answered') return null;
    const message = trailingUserMessage(messages);
    if (decision === 'running') {
        const started = runStartedAtMs(message);
        const stale = now - started >= PENDING_TURN_STALE_MS;
        return { decision, message, status: stale ? 'stale' : 'waiting', autoResend: false, force: true };
    }
    // never_started
    if (marker && Number.isFinite(marker.at)) {
        const fresh = now - marker.at < RESEND_GRACE_MS;
        return { decision, message, status: fresh ? 'sending' : 'stale', autoResend: false, force: false };
    }
    const sentAt = toMs(message.timestamp);
    const recent = sentAt !== null && now - sentAt < AUTO_RESEND_MAX_AGE_MS;
    return { decision, message, status: recent ? 'sending' : 'stale', autoResend: recent, force: false };
}

/** This tab's re-send marker for a stored user message: `{at}` or null. */
export function readResendMarker(messageDbId) {
    if (!messageDbId) return null;
    try {
        const raw = window.sessionStorage.getItem(MARKER_PREFIX + messageDbId);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && Number.isFinite(parsed.at) ? parsed : null;
    } catch (_) {
        return null;
    }
}

/** Record that this tab re-sent the message (survives a reload of the tab). */
export function writeResendMarker(messageDbId, at = Date.now()) {
    if (!messageDbId) return;
    try {
        window.sessionStorage.setItem(MARKER_PREFIX + messageDbId, JSON.stringify({ at }));
    } catch (_) { /* storage unavailable: the server-side guard still holds */ }
}
